import { createSlice } from '@reduxjs/toolkit';
import { defaultSortParam, SORT_PARAM_KEY_LOCAL_STORAGE } from '@root/v2/components/_workspace/SortParam';
import { WorkspaceDocument } from 'scribe-web-shared/types/document';

import { archiveDocumentMetadata, deleteAllArchived, deleteDocumentMetadata } from '../../../slices/actions/documentMetadataActions';
import { signOut } from '../../../slices/authSlice';
import { updateFilePermission, updateScribeFile } from '../../../slices/scribeFileSlice';
import {
  clearTypeFilter,
  duplicateScribeFile,
  fetchMore,
  fetchWorkspace,
  moveFileIntoFolder,
  retryDuplicateScribeFile,
  setSortParam,
  setTypeFilter,
  updateSelectedDocumentsSection,
  WorkspaceState
} from './actions';

const getInitialValueFromLocalStorage = (localStorageKey: string, defaultVal: any) => {
  if (typeof window === 'undefined') {
    return defaultVal;
  }

  try {
    const stored = localStorage.getItem(localStorageKey);

    return stored ? JSON.parse(stored) : defaultVal;
  } catch (error) {
    return defaultVal;
  }
};

const initialState: WorkspaceState = {
  documents: [],
  totalFiles: 0,
  loading: false,
  loadingMore: false,
  category: undefined,
  userOwnerFilter: null,
  sortParam: getInitialValueFromLocalStorage(SORT_PARAM_KEY_LOCAL_STORAGE, defaultSortParam),
  selectedDocumentsSection: 'documents'
};

export const workspaceSlice = createSlice({
  name: 'workspace',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(setTypeFilter, (state, action) => {
        state.typeFilter = action.payload;
      })
      .addCase(clearTypeFilter, (state) => {
        delete state.typeFilter;
      })
      .addCase(setSortParam, (state, action) => {
        state.sortParam = action.payload;

        if (typeof window !== 'undefined') {
          localStorage.setItem(SORT_PARAM_KEY_LOCAL_STORAGE, JSON.stringify(action.payload));
        }
      })
      .addCase(fetchWorkspace.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(fetchWorkspace.fulfilled, (state, action) => {
        state.documents = action.payload.documents;
        state.totalFiles = action.payload.total_files;
        state.category = action.meta.arg.category;
        state.loading = false;
      })
      .addCase(archiveDocumentMetadata.fulfilled, (state, action) => {
        const id = action.meta.arg;

        state.documents = state.documents.filter((doc) => doc.id !== id);
      })
      .addCase(deleteAllArchived.fulfilled, (state, action) => {
        state.documents = [];
      })
      .addCase(deleteDocumentMetadata.fulfilled, (state, action) => {
        const id = action.meta.arg;

        state.documents = state.documents.filter((doc) => doc.id !== id);
      })
      .addCase(moveFileIntoFolder.pending, (state, action) => {
        const { fileId } = action.meta.arg;

        state.documents = state.documents.filter((item) => item.id !== fileId);
      })
      .addCase(fetchMore.pending, (state) => {
        state.loadingMore = true;
      })
      .addCase(fetchMore.fulfilled, (state, action) => {
        state.documents.push(...action.payload.documents);
        state.totalFiles = action.payload.total_files;
        state.loadingMore = false;
      })
      .addCase(duplicateScribeFile.pending, (state) => {
        state.loadingMore = true;
      })
      .addCase(duplicateScribeFile.fulfilled, (state, action) => {
        const { retry } = action.meta.arg;

        state.documents = [...(retry ? [] : [action.payload]), ...state.documents];
        state.totalFiles += 1;
        state.loadingMore = false;
      })
      .addCase(retryDuplicateScribeFile.pending, (state, action) => {
        const { id } = action.meta.arg;

        state.loadingMore = true;
        state.documents = state.documents.map((document) =>
          document.id === id ? ({ ...document, duplicate_status: 'inprogress' } as WorkspaceDocument) : document
        );
      })
      .addCase(retryDuplicateScribeFile.fulfilled, (state, action) => {
        const { id } = action.meta.arg;

        state.documents = state.documents.map((document) => (document.id === id ? action.payload : document));
        state.loadingMore = false;
      })
      .addCase(updateFilePermission.pending, (state, action) => {
        // @ts-expect-error - here because scribeFileSlice is not .ts
        const { document, bodyParams } = action.meta.arg;
        const permissionLevel = bodyParams?.global_permission;

        state.documents = state.documents.map((doc) =>
          doc.id === document.id ? { ...doc, global_permission: permissionLevel || doc.global_permission } : doc
        );
      })
      .addCase(updateFilePermission.fulfilled, (state, action) => {
        const updatedDocument = action.payload as WorkspaceDocument;

        state.documents = state.documents.map((document) => (document.id === updatedDocument.id ? updatedDocument : document));
      })
      .addCase(updateFilePermission.rejected, (state, action) => {
        // @ts-expect-error - here because scribeFileSlice is not .ts
        const { document } = action.meta.arg;
        const documentWithoutUpdate = document as WorkspaceDocument;

        state.documents = state.documents.map((document) =>
          document.id === documentWithoutUpdate.id ? { ...document, global_permission: documentWithoutUpdate.global_permission } : document
        );
      })
      .addCase(updateScribeFile.fulfilled, (state, action) => {
        state.documents = state.documents.map((document) => {
          if (document.id === action.payload.id) {
            return action.payload as WorkspaceDocument;
          }
          return document;
        });
      })
      .addCase(signOut, () => initialState)
      .addCase(updateSelectedDocumentsSection, (state, action) => {
        state.selectedDocumentsSection = action.payload;
      });
  }
});

export default workspaceSlice.reducer;
